<template>
  <div class="map-kit-geocoder">
    <FontAwesomeIcon icon="magnifying-glass" class="map-kit-geocoder-icon" />
    <AutoComplete
      ref="autoCompleteRef"
      v-model="searchText"
      :suggestions="suggestions"
      :loading="loading"
      :delay="250"
      :placeholder="$t('geocoder.searchPlaceholder')"
      scroll-height="280px"
      option-label="displayName"
      fluid
      :pt="{
        root: { class: ['map-kit-autocomplete', { 'map-kit-autocomplete--has-value': !!searchText }] },
      }"
      @complete="onSearch"
      @option-select="onSelect"
    >
      <template #option="{ option }">
        <div class="map-kit-geocoder-option">
          <template v-if="option.type === 'coords'">
            <span class="font-semibold">{{ option.displayName }}</span>
            <small class="text-gray-400 ml-1">{{ $t('geocoder.coordinates') }}</small>
          </template>
          <template v-else>
            <div class="flex flex-col">
              <span>
                <strong>{{ option.primaryText }}</strong>
                <span v-if="option.placeType" class="text-sm text-gray-400 capitalize ml-1">{{ option.placeType }}</span>
              </span>
              <small v-if="option.secondaryText" class="text-gray-500">{{ option.secondaryText }}</small>
            </div>
          </template>
        </div>
      </template>
      <template #empty>
        <div v-if="hasSearched" class="p-2 text-sm">
          {{ $t('geocoder.noResults') }} <strong>{{ lastQuery }}</strong>
        </div>
      </template>
    </AutoComplete>

    <Button
      v-if="searchText"
      icon="fas fa-delete-left"
      severity="contrast"
      text
      size="small"
      class="map-kit-geocoder-clear"
      :aria-label="$t('geocoder.clearSearch')"
      @click="clearSearch"
    />

    <Button
      v-tooltip.bottom="$t('geocoder.trackGeolocation')"
      icon="fas fa-location-crosshairs"
      :severity="tracking ? 'primary' : 'contrast'"
      :class="{ 'toolbar-item-selected': tracking }"
      text
      size="small"
      class="map-kit-geocoder-locate"
      :aria-label="$t('geocoder.toggleGeolocation')"
      @click="toggleGeolocation"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Icon, Style } from 'ol/style';
import { fromLonLat, transformExtent } from 'ol/proj';

import { useMap } from '@/composables/useMap';
import { useGeolocation } from '@/composables/useGeolocation';

interface GeocoderSuggestion {
  displayName: string;
  primaryText: string;
  secondaryText: string;
  placeType: string;
  type: 'place' | 'coords';
  center: [number, number]; // [lon, lat]
  bbox?: [number, number, number, number];
}

const { map, MAPTILER_KEY } = useMap();
const { tracking, toggleGeolocation } = useGeolocation();

const searchText = ref('');
const suggestions = ref<GeocoderSuggestion[]>([]);
const loading = ref(false);
const hasSearched = ref(false);
const lastQuery = ref('');
const autoCompleteRef = ref<InstanceType<typeof AutoComplete> | null>(null);

let searchMarkerLayer: VectorLayer | null = null;

/** Parse direct coordinate input (decimal degrees) */
const parseCoordinates = (input: string): [number, number] | null => {
  // Match patterns like "48.137, 11.576" or "11.576 48.137"
  const match = input.match(/^\s*(-?\d+\.?\d*)\s*[,\s]\s*(-?\d+\.?\d*)\s*$/);
  if (!match) return null;
  const a = parseFloat(match[1]);
  const b = parseFloat(match[2]);
  if (isNaN(a) || isNaN(b)) return null;
  // If first looks like lat (abs <= 90), treat as lat,lon
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [b, a]; // [lon, lat]
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180) return [a, b]; // [lon, lat]
  return null;
};

const onSearch = async (event: { query: string }) => {
  const query = event.query.trim();
  if (query.length < 2) {
    suggestions.value = [];
    return;
  }

  lastQuery.value = query;
  loading.value = true;
  hasSearched.value = false;

  const results: GeocoderSuggestion[] = [];

  // Check for direct coordinate input
  const coords = parseCoordinates(query);
  if (coords) {
    results.push({
      displayName: `${coords[1].toFixed(5)}°, ${coords[0].toFixed(5)}°`,
      primaryText: `${coords[1].toFixed(5)}°, ${coords[0].toFixed(5)}°`,
      secondaryText: '',
      placeType: 'coordinates',
      type: 'coords',
      center: coords,
    });
  }

  // MapTiler geocoding
  try {
    const bbox = map.value?.getView().calculateExtent(map.value.getSize());
    let url = `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${MAPTILER_KEY}`;
    if (bbox) {
      const wgs = transformExtent(bbox, 'EPSG:3857', 'EPSG:4326');
      url += `&bbox=${wgs.join(',')}`;
    }

    const res = await fetch(url);
    const data = await res.json();

    if (data.features) {
      for (const f of data.features) {
        const parts = (f.place_name || '').split(/,(.+)/);
        results.push({
          displayName: f.place_name || f.text,
          primaryText: parts[0] || f.text,
          secondaryText: (parts[1] || '').trim(),
          placeType: f.place_type?.[0] || '',
          type: 'place',
          center: f.center as [number, number],
          bbox: f.bbox as [number, number, number, number] | undefined,
        });
      }
    }
  } catch {
    // Silently fail — network errors are non-fatal
  }

  suggestions.value = results;
  loading.value = false;
  hasSearched.value = true;
};

const onSelect = (event: { value: GeocoderSuggestion }) => {
  const item = event.value;
  if (!map.value) return;

  // Clear previous marker
  removeSearchMarker();

  // Place a marker
  const coord = fromLonLat(item.center);
  const feature = new Feature(new Point(coord));
  feature.setStyle(
    new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: 'data:image/svg+xml,' + encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#3b82f6"/>
            <circle cx="12" cy="12" r="5" fill="white"/>
          </svg>`
        ),
        scale: 1,
      }),
    }),
  );

  const source = new VectorSource({ features: [feature] });
  searchMarkerLayer = new VectorLayer({ source, zIndex: 950 });
  map.value.addLayer(searchMarkerLayer);

  // Zoom to result
  if (item.bbox) {
    const extent = transformExtent(item.bbox, 'EPSG:4326', 'EPSG:3857');
    map.value.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
  } else {
    map.value.getView().animate({
      center: coord,
      zoom: 14,
      duration: 500,
    });
  }

  searchText.value = item.displayName;
};

const removeSearchMarker = () => {
  if (searchMarkerLayer && map.value) {
    map.value.removeLayer(searchMarkerLayer);
    searchMarkerLayer = null;
  }
};

const clearSearch = () => {
  searchText.value = '';
  suggestions.value = [];
  hasSearched.value = false;
  removeSearchMarker();
};

// Ctrl+K / Cmd+K shortcut to focus search
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      const el = autoCompleteRef.value as any;
      el?.$el?.querySelector('input')?.focus();
    }
  });
}
</script>
